// @flow
import type PostProcessingRule from "./PostProcessingRule";
import InspectionRegion, {InspectionRegionDecodeMethods} from "./InspectionRegion";
import {PostProcessingRuleTypes} from "./PostProcessingRule";
import {LIValidationError} from "./LIValidationError";
import type SampleBasic from "./SampleBasic";

export default class Profile {
    
    constructor() {
        this.id = 0;
        this.holdsSampleType=0;     // version1 profiles have a holdsSampleType
        this.subType=0;             // version2 profiles have a subType
        this.name = "";
        this.comment = "";
        this.type = 0;
        
        this.destinationMagProfileId = -1;
        this.isCaseMappedExternal = false;
        this.isSampleNumMappedLater = false;
        this.barcodesAreUnique = false;
        this.noCaseSampleRequired = false;
        
        this.sampleImage = null;
        this.enabled = true;
        
        this.inspectionRegions = [];
    }
    
    get IsMagProfile() {
        return this.type === 0;
    }
    
    FromJson(json) {
        this.id = json.id;
        this.holdsSampleType = json.subType??json.holdsSampleType??0;  // we cover off V2 profiles and v1 profiles here
        this.subType = json.subType??0;
        this.name = json.name;
        this.comment = json.comment;
        this.type = json.type;
        this.destinationMagProfileId = json.destinationMagProfileId;
        this.isCaseMappedExternal = json.isCaseMappedExternal;
        this.isSampleNumMappedLater = json.isSampleNumMappedLater;
        this.noCaseSampleRequired = json.noCaseSampleRequired;
        this.barcodesAreUnique = json.barcodesAreUnique;
        this.sampleImage = json.sampleImage;
        this.enabled = json.enabled;
        
        if (json.inspectionRegions) {
            this.inspectionRegions = json.inspectionRegions.map((ir) => {
                const inspectionRegion = new InspectionRegion();
                inspectionRegion.FromJson(ir);
                return inspectionRegion;
            });
        }
    }
    
    GetBarcodeProcessingRule(): PostProcessingRule {
        if (!this.inspectionRegions || this.inspectionRegions.length === 0) {
            throw new LIValidationError("No inspection regions defined for this profile");
        }
        
        for (const ir:InspectionRegion of this.inspectionRegions) {
            if (ir.decodeMethod===InspectionRegionDecodeMethods.DATA_MATRIX ||
                    ir.decodeMethod===InspectionRegionDecodeMethods.LINEAR) {
                if (!ir.postProcessing || ir.postProcessing.type === PostProcessingRuleTypes.NONE) {
                    throw new LIValidationError("No post processing rule defined for this profile");
                }
                return ir.postProcessing;
            }
        }
        return null;
    }
    
    BarcodeDoesMatch(barcode:string) {
        const rule = this.GetBarcodeProcessingRule();
        if (!rule) {
            return false;
        }
        try {
            console.log("running barcode check against rule "+rule.name+" type "+rule.type + " for barcode "+barcode);
            if (this.IsMagProfile) {
                rule.EvaluateAsMag(barcode); // throws if rule fails
            }
            else {
                rule.EvaluateBaseValidationAgainst(barcode); // throws if rule fails
            }
        }
        catch (e) {
            if (e instanceof LIValidationError) {
                console.debug("rule post processing failed");
                console.debug("BarcodeDoesMatch(): "+e.message);
                return false;
            }
        }
        
        // get here if validation passed
        return true;
    }

    /**
     * return a list of validation strings for all post processing rules in this profile
     * @returns {[]}
     * @constructor
     */
    GetValidations() {
        const validations = [];
        for (const ir:InspectionRegion of this.inspectionRegions) {
            if (ir.postProcessing && ir.postProcessing.validation) {
                validations.push(ir.postProcessing.validation);
            }
        }
        return validations;
    }

    /**
     * return the validations list as a single string
     * @constructor
     */
    GetValidationsAsString() {
        
        const validations = this.GetValidations();
        if (validations.length === 0) {
            return "";
        }
        return validations.join(",");
    }
    
    GetHintsAsString() {
        const hints = [];
        for (const ir:InspectionRegion of this.inspectionRegions) {
            let hint = null;
            if (ir.postProcessing && ir.postProcessing.caseExample) {
                hint = `Case: ${ir.postProcessing.caseExample}`;
            }
            if (ir.postProcessing && ir.postProcessing.sampleExample) {
                hint = hint ? hint + ", " : "";
                hint += `Sample: ${ir.postProcessing.sampleExample}`;
            }
            if (hint === null) {
                hint = "No examples available";
            }
            hints.push(hint);
        }
        if (hints.length === 0) {
            return "";
        }
        return hints.join(",");
    }

    /**
     * check to see that the sample has everything it needs to be processed through this profile
     * The case/sample are mapped out if required.
     * 
     * @param sample
     * @returns {SampleBasic}
     * @constructor
     * @throws LIValidationError if the sample is missing critical information
     * @throws CaseBreakdownError if the case/sample were supposed to be mapped but could not be
     */
    MapSampleFieldsAndCheck(sample:SampleBasic):SampleBasic {
        if (!sample.profileId || sample.profileId===0) { // this is critical
            console.debug("SampleHasAllRequiredInfo(): sample is missing profileId - fail");
            throw new LIValidationError("Sample is missing profileId");
        }
        const rule = this.GetBarcodeProcessingRule();
        if (!rule) {
            throw new LIValidationError("No Post Processing Rule found for profile "+this.name);
        }

        // set some particulars for this sample
        sample.sampleType = this.holdsSampleType;
        if(this.subType && this.subType>0) { // this is a v2 profile
            console.debug("SampleHasAllRequiredInfo(): appears to be V2 profile - setting subType to "+this.subType);
            sample.sampleType = this.subType;
        }
        
        if (!this.isCaseMappedExternal) { // we need to TRY to map the case AND/OR sample numbers now
            sample = rule.SetCaseAndSampleNumbersIfPossible(sample); // throws -- *** side effect here as the sample info is changed
        }
        
        return sample; // updated sample
    }
}